// Shared client for the same aserver/api gateway the mobile app uses.
// Keeping the website on this one backend (instead of its own separate
// Prisma/Stripe/Adyen backend) is what makes movies/showtimes/seats/tickets
// actually match the app - a ticket bought here is a real row in the same
// user_tickets table the app reads from.

const API_BASE = process.env.REACT_APP_API_BASE || 'https://api.starviewcinema.com';
const GATEWAY_BASE = `${API_BASE}/api/api/v1`;

// The fixed showtime slots offered per movie - mirrors the exact list used
// by the mobile app (app/Screens/popups/USA7/Seatselection/seatselection*.js).
// There's no separate "showtime" database entity in the real system; a
// showtime is just one of these time strings.
export const SHOWTIMES = ['11:00 AM', '1:45 PM', '4:30 PM', '7:00 PM', '8:30 PM', '9:45 PM', '10:30 PM', '11:45 PM'];
// Matches timeslot1.js's fixed seat grid - every real auditorium is rendered
// as this same 6x10 grid regardless of its stored capacity.
export const SEAT_ROWS = 6;
export const SEATS_PER_ROW = 10;
export const TICKET_PRICE_CENTS = 1150; // $11.50/ticket - must match the gateway's TICKET_PRICE_CENTS

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...options,
  });
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    // no JSON body
  }
  if (!response.ok) {
    const message = (data && (data.message || data.error)) || `Request failed (${response.status})`;
    const err = new Error(message);
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

// authFetch: same as request(), but with a Bearer token attached - pass the
// authFetch from useAuth() for anything that requires a signed-in account
// (assigning an auditorium, checking seats, checkout).
async function authedRequest(authFetch, url, options = {}) {
  const response = await authFetch(url, options);
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    // no JSON body
  }
  if (!response.ok) {
    const message = (data && (data.message || data.error)) || `Request failed (${response.status})`;
    const err = new Error(message);
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

// --- Movies (public, read-only) --------------------------------------------

export async function fetchMovies({ q = '', from = 1, size = 20, country = '' } = {}) {
  const params = new URLSearchParams({ q, from: String(from), size: String(size) });
  if (country) params.set('country', country);
  const data = await request(`${API_BASE}/api/movies?${params.toString()}`);
  return data.movies || [];
}

export async function fetchMovie(moviePublicId) {
  const data = await request(`${API_BASE}/api/movies/public/${moviePublicId}`);
  return data.movie;
}

// --- Auditorium assignment / seats / checkout (real system, same as the app) -

export async function assignAuditorium(authFetch, { moviePublicId, country, theaterNumber, showtime }) {
  const data = await authedRequest(authFetch, `${API_BASE}/api/showtimes/assign-auditorium`, {
    method: 'POST',
    body: JSON.stringify({ moviePublicId, country, theaterNumber, showtime }),
  });
  return data.auditorium;
}

export async function getReservedSeats(authFetch, { movieId, showtime, auditoriumId }) {
  const params = new URLSearchParams({ movieId, showtime, auditoriumId });
  const data = await authedRequest(authFetch, `${API_BASE}/api/tickets/reserved-seats?${params.toString()}`);
  return data.reservedSeats || [];
}

export async function createCheckoutIntent(authFetch, { seats, promoCode, giftCardCode, country }) {
  return authedRequest(authFetch, `${API_BASE}/api/tickets/checkout/create-intent`, {
    method: 'POST',
    body: JSON.stringify({ seats, promoCode, giftCardCode, country }),
  });
}

export async function confirmCheckout(authFetch, payload) {
  return authedRequest(authFetch, `${API_BASE}/api/tickets/checkout/confirm`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// --- Gift cards (real Stripe purchase, redeemable at ticket checkout) -------

export async function createGiftCardIntent(authFetch, { amountCents }) {
  return authedRequest(authFetch, `${API_BASE}/api/giftcards/checkout/create-intent`, {
    method: 'POST',
    body: JSON.stringify({ amountCents }),
  });
}

export async function confirmGiftCardCheckout(authFetch, payload) {
  return authedRequest(authFetch, `${API_BASE}/api/giftcards/checkout/confirm`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function redeemGiftCard(authFetch, { code }) {
  return authedRequest(authFetch, `${API_BASE}/api/giftcards/redeem`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function fetchMyGiftCards(authFetch) {
  const data = await authedRequest(authFetch, `${API_BASE}/api/giftcards/mine`);
  return data.giftCards || [];
}

export async function fetchPurchasedGiftCards(authFetch) {
  const data = await authedRequest(authFetch, `${API_BASE}/api/giftcards/purchased`);
  return data.giftCards || [];
}

// --- Promotions (public, real server-enforced discount codes) --------------

export async function fetchActivePromotions() {
  const data = await request(`${API_BASE}/api/promotions/active`);
  return data.promotions || [];
}

// --- Driver applications (public - applicants don't have an account yet) ---

export async function fetchDriverDocumentRequirements(countryCode) {
  const params = new URLSearchParams({ country: countryCode || '' });
  return request(`${API_BASE}/api/driver-applications/requirements?${params.toString()}`);
}

// Multipart upload - deliberately not routed through request() above, which
// hardcodes a JSON Content-Type header that would break the multipart
// boundary the browser needs to set itself for FormData.
export async function uploadDriverDocument(file) {
  const formData = new FormData();
  formData.append('document', file);
  const response = await fetch(`${API_BASE}/api/driver-applications/documents`, {
    method: 'POST',
    body: formData,
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = (data && (data.message || data.error)) || `Upload failed (${response.status})`;
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  return data;
}

export async function submitDriverApplication(payload) {
  return request(`${API_BASE}/api/driver-applications`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// --- Restaurant partner applications (public) -------------------------------

export async function submitRestaurantApplication(payload) {
  return request(`${API_BASE}/api/restaurant-applications`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// --- Display helpers ---------------------------------------------------------

export function formatRuntime(minutes) {
  if (!minutes && minutes !== 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function mapMovieForCard(movie) {
  return {
    // moviePublicId is the real identifier used everywhere else in the
    // system (ticket rows, auditorium assignment) - previously this fell
    // back to Mongo's internal _id, which meant "Get Tickets" links didn't
    // actually correspond to a real, bookable movie.
    id: movie.moviePublicId,
    moviePublicId: movie.moviePublicId,
    country: movie.country,
    theaterNumber: movie.theaterNumber,
    title: movie.movieTitle,
    image_url: movie.moviePoster || movie.movieImage,
    runtime: formatRuntime(movie.movieslength),
    // Real MPAA rating (e.g. "PG-13") - previously mislabeled from
    // movie.genres, which is a different field entirely.
    rating: movie.rating,
    genres: movie.genres,
    trailer_url: movie.trailerUrl || null,
    // Real TMDB audience score (0-10), backfilled onto every film in the
    // catalog - no more decorative stars over a hardcoded zero.
    // Scores, in order of trust: the STARVIEW SCORE (✦) - our own audience
    // metric computed from real completions and admissions on this platform -
    // then the TMDB audience score as fallback with its minimum-votes floor
    // (a 9/10 from one voter is noise, not a rating). No data, no stars.
    audience_score: movie.svScore != null ? `✦ ${Math.round(movie.svScore)}%`
      : movie.audienceScore > 0 && movie.audienceVotes >= 5 ? `${Math.round(movie.audienceScore * 10)}%` : null,
    audience_stars: movie.svScore != null ? Math.round(movie.svScore / 10) / 2
      : movie.audienceScore > 0 && movie.audienceVotes >= 5 ? Math.round(movie.audienceScore) / 2 : null,
    audience_votes: movie.audienceVotes || 0,
  };
}

export { API_BASE, GATEWAY_BASE };


// ==================== Account hub ====================
export async function fetchActiveTickets(authFetch) {
  const d = await authedRequest(authFetch, `${API_BASE}/api/tickets/active`);
  return d.tickets || d || [];
}
export async function fetchTicketHistory(authFetch) {
  const d = await authedRequest(authFetch, `${API_BASE}/api/tickets/history`);
  return d.tickets || d || [];
}
export async function subscribeStarviewX(authFetch, billingCycle) {
  return authedRequest(authFetch, `${API_BASE}/api/api/v1/auth/currentuser/starviewx/subscribe`, {
    method: 'POST',
    body: JSON.stringify({ billingCycle }),
  });
}
